import { initContract } from '@ts-rest/core';
import { z } from 'zod';
////////////////////////////////////////////////////////////////////////////////
// getClaimContract
////////////////////////////////////////////////////////////////////////////////
export const GetClaimContractResponse = z.strictObject({
    contractNumber: z.string().nullable(),
    tenant: z.string().nullable(),
    brokerName: z.string().nullable(),
    policyholderName: z.string().nullable(),
    divisionHeader: z.string().nullable(),
    risk: z.string().nullable(),
    objectHeader: z.string().nullable(),
    termsAndConditions: z.string().nullable(),
    status: z.string().nullable(),
});
////////////////////////////////////////////////////////////////////////////////
// getClaimData
////////////////////////////////////////////////////////////////////////////////
export const GetClaimDataResponse = z.strictObject({
    claimNumber: z.string().nullable(),
    claimDate: z.string().nullable(),
    reportDate: z.string().nullable(),
    claimType: z.string().nullable(),
    claimCause: z.string().nullable(),
    claimAmountEur: z.number().nullable(),
    claimStatus: z.string().nullable(),
    claimProcessedBy: z.string().nullable(),
    clerkName: z.string().nullable(),
    clerkPhone: z.string().nullable(),
    clerkEmail: z.string().nullable(),
    insurerData: z
        .strictObject({
        claimNumber: z.string(),
        insurer: z.string().nullable(),
        clerkName: z.string().nullable(),
        clerkPhone: z.string().nullable(),
    })
        .nullable(),
});
////////////////////////////////////////////////////////////////////////////////
// getPaymentDocuments
////////////////////////////////////////////////////////////////////////////////
export const ClaimPaymentDocument = z.strictObject({
    id: z.string(),
    processedAt: z.string(),
    documentType: z.string(),
    documentAmount: z.number(),
    decision: z.string(),
    payoutAmount: z.number().nullable(),
});
export const GetClaimPaymentDocumentsResponse = z.strictObject({
    documents: z.array(ClaimPaymentDocument),
});
////////////////////////////////////////////////////////////////////////////////
// getCommunicationItems
////////////////////////////////////////////////////////////////////////////////
export const CommunicationItem = z.strictObject({
    id: z.string(),
    direction: z.enum(['inbound', 'outbound']),
    date: z.string(),
    content: z.array(z.strictObject({
        documentIds: z.array(z.string()),
        type: z.string(),
    })),
});
export const GetCommunicationItemsResponse = z.strictObject({
    items: z.array(CommunicationItem),
});
////////////////////////////////////////////////////////////////////////////////
// submitAdditionalDocuments
////////////////////////////////////////////////////////////////////////////////
export const SubmitAdditionalDocumentsRequest = z.strictObject({
    message: z.string().min(1),
    files: z.array(z.strictObject({
        hash: z.string(),
        name: z.string(),
        mimetype: z.string(),
    })),
});
export const SubmitAdditionalDocumentsResponse = z.strictObject({
    success: z.boolean(),
});
////////////////////////////////////////////////////////////////////////////////
// getFileUploadUrl
////////////////////////////////////////////////////////////////////////////////
export const GetFileUploadUrlRequest = z.strictObject({
    fileHash: z.string().min(1),
});
export const GetFileUploadUrlResponse = z.strictObject({
    uploadUrl: z.string().url(),
    blobPath: z.string(),
});
////////////////////////////////////////////////////////////////////////////////
// contract
////////////////////////////////////////////////////////////////////////////////
const c = initContract();
export const claimDetailsContract = c.router({
    getClaimContract: {
        method: 'GET',
        path: '/get-claim-contract/:claimId',
        pathParams: z.strictObject({
            claimId: z.string(),
        }),
        responses: {
            200: GetClaimContractResponse,
        },
    },
    getClaimData: {
        method: 'GET',
        path: '/get-claim-data/:claimId',
        pathParams: z.strictObject({
            claimId: z.string(),
        }),
        responses: {
            200: GetClaimDataResponse,
        },
    },
    getPaymentDocuments: {
        method: 'GET',
        path: '/get-payment-documents/:claimId',
        pathParams: z.strictObject({
            claimId: z.string(),
        }),
        responses: {
            200: GetClaimPaymentDocumentsResponse,
        },
    },
    getCommunicationItems: {
        method: 'GET',
        path: '/get-communication-items/:claimId',
        pathParams: z.strictObject({
            claimId: z.string(),
        }),
        responses: {
            200: GetCommunicationItemsResponse,
        },
    },
    submitAdditionalDocuments: {
        method: 'POST',
        path: '/submit-additional-documents/:claimId',
        pathParams: z.strictObject({
            claimId: z.string(),
        }),
        body: SubmitAdditionalDocumentsRequest,
        responses: {
            200: SubmitAdditionalDocumentsResponse,
        },
    },
    getFileUploadUrl: {
        method: 'POST',
        path: '/get-file-upload-url/:claimId',
        pathParams: z.strictObject({
            claimId: z.string(),
        }),
        body: GetFileUploadUrlRequest,
        responses: {
            200: GetFileUploadUrlResponse,
        },
    },
}, { strictStatusCodes: true, pathPrefix: '/claim-details' });
